import { Link } from 'react-router-dom';
import { ArrowLeftIcon, TrashIcon } from 'lucide-react';
import Button from '@/components/form/Button';
import { useAuth } from '@/context/AuthContext';
import DeleteAccountModal from '@/components/profile/DeleteAccountModal';
import { useState } from 'react';

export default function PrivacyPolicy() {
  const { user } = useAuth();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white shadow-lg rounded-xl my-10">
      {!user ? (
        <Button
          variant="ghost"
          size="sm"
          icon={<ArrowLeftIcon className="h-4 w-4" />}
          onClick={() => (window.location.href = '/')}
          className="mb-6"
        >
          Back to Home
        </Button>
      ) : (
        <div className="flex justify-between items-center mb-6">
          <Button
            variant="ghost"
            size="sm"
            icon={<ArrowLeftIcon className="h-4 w-4" />}
            onClick={() => (window.location.href = '/dashboard')}
          >
            Back to Dashboard
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={<TrashIcon className="h-4 w-4" />}
            onClick={() => setShowDeleteModal(true)}
            className="bg-red-600 hover:bg-red-700 focus:ring-red-500"
          >
            Delete Account
          </Button>
        </div>
      )}

      <h1 className="text-4xl font-extrabold text-blue-600 mb-10 text-center">Privacy Policy</h1>

      <div className="prose prose-lg prose-blue max-w-none">
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 border-b pb-2">1. Controller</h2>
          <p className="mt-4 text-gray-700">
            The controller responsible for data processing on this website according to the GDPR is:
          </p>
          <address className="not-italic mt-4 p-4 bg-gray-50 rounded-lg">
            <strong>Dennis Horn</strong>
            <br />
            Wadgasserstr. 112
            <br />
            66787 Wadgassen
            <br />
            Germany
            <br />
            <br />
            Email: <a href="mailto:info@snapvote.ai" className="text-primary-600 hover:text-primary-700">info@snapvote.ai</a>
          </address>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 border-b pb-2">2. Data Collection</h2>
          <p className="mt-4 text-gray-700">We collect and process the following data:</p>
          <ul className="mt-4 list-disc pl-6 text-gray-700">
            <li>Email address (for authentication)</li>
            <li>Username and profile information</li>
            <li>Content you create (SNAPs, votes, comments)</li>
            <li>Usage data and analytics</li>
            <li>Technical data (IP address, browser information)</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 border-b pb-2">3. Purpose of Processing</h2>
          <p className="mt-4 text-gray-700">Your data is processed for the following purposes:</p>
          <ul className="mt-4 list-disc pl-6 text-gray-700">
            <li>Providing and maintaining our services</li>
            <li>User authentication and account management</li>
            <li>Enabling community features and interactions</li>
            <li>Improving user experience</li>
            <li>Security and fraud prevention</li>
            <li>Analyzing user behavior and improving the service using Google Analytics (only with your consent)</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 border-b pb-2">4. Legal Basis</h2>
          <p className="mt-4 text-gray-700">We process your data based on:</p>
          <ul className="mt-4 list-disc pl-6 text-gray-700">
            <li>Your consent (Art. 6(1)(a) GDPR)</li>
            <li>Contract fulfillment (Art. 6(1)(b) GDPR)</li>
            <li>Legal obligations (Art. 6(1)(c) GDPR)</li>
            <li>Legitimate interests (Art. 6(1)(f) GDPR), especially for improving our services</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 border-b pb-2">5. Data Recipients</h2>
          <p className="mt-4 text-gray-700">Your data may be shared with:</p>
          <ul className="mt-4 list-disc pl-6 text-gray-700">
            <li>Service providers (hosting, analytics – including Google Analytics)</li>
            <li>Other users (for public profile information)</li>
            <li>Legal authorities when required by law</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 border-b pb-2">6. Google Analytics</h2>
          <p className="mt-4 text-gray-700">
            We use Google Analytics, a web analytics service provided by Google LLC, to understand how users interact with our website. Google Analytics collects data such as:
          </p>
          <ul className="mt-4 list-disc pl-6 text-gray-700">
            <li>Page views</li>
            <li>Clicks</li>
            <li>User flows</li>
            <li>Browser and device information</li>
            <li>IP addresses (anonymized)</li>
          </ul>
          <p className="mt-4 text-gray-700">
            We only activate Google Analytics after you have given your explicit consent. The information generated is typically transmitted to a Google server in the USA and stored there. We have configured Google Analytics to anonymize IP addresses.
          </p>
          <p className="mt-4 text-gray-700">
            You can revoke your consent at any time by adjusting your cookie preferences.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 border-b pb-2">7. Your Rights</h2>
          <p className="mt-4 text-gray-700">You have the following rights:</p>
          <ul className="mt-4 list-disc pl-6 text-gray-700">
            <li>Right to access your data</li>
            <li>Right to rectification</li>
            <li>Right to erasure ("right to be forgotten")</li>
            <li>Right to restrict processing</li>
            <li>Right to data portability</li>
            <li>Right to object</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 border-b pb-2">8. Contact</h2>
          <p className="mt-4 text-gray-700">
            For any privacy-related questions or requests, please contact us at:{' '}
            <a href="mailto:info@snapvote.ai" className="text-primary-600 hover:text-primary-700">info@snapvote.ai</a>
          </p>
        </section>

        <footer className="mt-12 pt-8 border-t text-center text-gray-500">
          Last updated: March 30, 2025
        </footer>
      </div>

      {user && (
        <DeleteAccountModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
}